import { useEffect, useRef, useState } from 'react';
import { HakenIcon, MuellIcon, UnterschriftIcon } from '../icons';

interface SignaturFeldProps {
	name: string;
	wert?: string;
	onAendern: (datenUrl: string | undefined) => void;
}

/**
 * Unterschrift per Finger oder Maus, auf einem Canvas mit weißem Hintergrund
 * (damit sie im gedruckten Dokument wie echte Tinte aussieht, auch wenn die
 * App selbst dunkel ist). Zeichnet direkt in Bildschirmauflösung
 * (devicePixelRatio), sonst wirken Unterschriften auf Retina-Displays verwaschen.
 */
export function SignaturFeld({ name, wert, onAendern }: SignaturFeldProps) {
	const canvasRef = useRef<HTMLCanvasElement | null>(null);
	const zeichnetRef = useRef(false);
	const letztePosition = useRef<{ x: number; y: number } | null>(null);
	const [hatGezeichnet, setHatGezeichnet] = useState(false);

	useEffect(() => {
		if (wert) return;
		const canvas = canvasRef.current;
		if (!canvas) return;
		const dpr = window.devicePixelRatio || 1;
		const breite = canvas.clientWidth;
		const hoehe = canvas.clientHeight;
		canvas.width = breite * dpr;
		canvas.height = hoehe * dpr;
		const ctx = canvas.getContext('2d');
		if (!ctx) return;
		ctx.scale(dpr, dpr);
		ctx.lineCap = 'round';
		ctx.lineJoin = 'round';
		ctx.lineWidth = 2.5;
		ctx.strokeStyle = '#161616';
	}, [wert]);

	function position(ereignis: React.PointerEvent<HTMLCanvasElement>) {
		const rect = ereignis.currentTarget.getBoundingClientRect();
		return { x: ereignis.clientX - rect.left, y: ereignis.clientY - rect.top };
	}

	function zeichnenStart(ereignis: React.PointerEvent<HTMLCanvasElement>) {
		ereignis.currentTarget.setPointerCapture(ereignis.pointerId);
		zeichnetRef.current = true;
		letztePosition.current = position(ereignis);
		setHatGezeichnet(true);
	}

	function zeichnenBewegen(ereignis: React.PointerEvent<HTMLCanvasElement>) {
		if (!zeichnetRef.current) return;
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext('2d');
		if (!ctx || !letztePosition.current) return;
		const aktuell = position(ereignis);
		ctx.beginPath();
		ctx.moveTo(letztePosition.current.x, letztePosition.current.y);
		ctx.lineTo(aktuell.x, aktuell.y);
		ctx.stroke();
		letztePosition.current = aktuell;
	}

	function zeichnenEnde() {
		zeichnetRef.current = false;
		letztePosition.current = null;
	}

	function loeschen() {
		const canvas = canvasRef.current;
		const ctx = canvas?.getContext('2d');
		if (ctx && canvas) ctx.clearRect(0, 0, canvas.width, canvas.height);
		setHatGezeichnet(false);
	}

	function uebernehmen() {
		const canvas = canvasRef.current;
		if (!canvas) return;
		onAendern(canvas.toDataURL('image/png'));
	}

	if (wert) {
		return (
			<div className="signatur-karte signatur-karte--fertig">
				<div className="signatur-karte-kopf">
					<span>{name}</span>
					<span className="signatur-erledigt">
						<HakenIcon width={14} height={14} /> Unterschrieben
					</span>
				</div>
				<img className="signatur-bild" src={wert} alt={`Unterschrift von ${name}`} />
				<button type="button" className="nebenknopf" onClick={() => onAendern(undefined)}>
					Neu unterschreiben
				</button>
			</div>
		);
	}

	return (
		<div className="signatur-karte">
			<div className="signatur-karte-kopf">
				<span>{name}</span>
			</div>
			<div className="signatur-flaeche-wrap">
				<canvas
					ref={canvasRef}
					className="signatur-flaeche"
					onPointerDown={zeichnenStart}
					onPointerMove={zeichnenBewegen}
					onPointerUp={zeichnenEnde}
					onPointerLeave={zeichnenEnde}
				/>
				{!hatGezeichnet && (
					<p className="signatur-platzhalter">
						<UnterschriftIcon width={16} height={16} /> Hier mit dem Finger unterschreiben
					</p>
				)}
			</div>
			<div className="signatur-knopfleiste">
				<button type="button" className="nebenknopf" onClick={loeschen} disabled={!hatGezeichnet}>
					<MuellIcon width={16} height={16} />
					Löschen
				</button>
				<button type="button" className="hauptknopf" onClick={uebernehmen} disabled={!hatGezeichnet}>
					Übernehmen
				</button>
			</div>
		</div>
	);
}
